import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Label from "../Label/Label";
import { Controller } from "react-hook-form";
import Select, { components, MenuListProps, StylesConfig } from "react-select";
import { customSelectInputStyles, customSelectInputStylesError } from "../../customSelectStyles";
import ErrorText from "../ErrorText/ErrorText";
import { SelectOptionT } from "../../../../models/models";
import { Control } from "react-hook-form/dist/types/form";
import Icon from "../../../Icon/Icon";
import { DirectionE, IconColorE, LangE } from "../../../../models/enums";
import Button, { ButtonModeE } from "../../../Button/Button";
import { Span } from "../../../Text/Text";
import { useAppSelector } from '../../../../store/hooks';
import { selectCurrentLang, selectDirection } from '../../../../store/selectors/appSelectors';
import { isMobileOrTablet } from '../../../../utils/helpers/mediaQueries';
import { useTranslate } from '../../../../utils/hooks/useTranslate';
import MenuList from './components/MenuList';

import styles from '../../form.module.scss';

type SelectFieldPropTypes = {
    name: string;
    label: string;
    options: SelectOptionT[];
    errorMessage: string;
    hintText?: string;
    control: Control<any, any>
    orderIndex: number;
    createNewOption?: (inputValue?: string) => void;
    style: any;
    props?: any;
    defaultValue: SelectOptionT;
    onChange: (value: any) => void;
    onInput: (value: any) => void;
    onBlur: () => void;
    isMulti: boolean;
    extraContent: any;
    menuFooterContent: any;
    closeMenuOnSelect: boolean;
    placeholder: string;
    addAction?: () => void;
    isClearable?: boolean;
    controlledValue?: SelectOptionT;
    customSelectPropsStyles?: (lang: LangE) => StylesConfig,
}

const SelectField: FC<SelectFieldPropTypes> = (props) => {
    const { t } = useTranslate();
    const {
        name,
        label,
        options,
        errorMessage,
        hintText,
        control,
        orderIndex,
        createNewOption,
        style,
        props: innerProps,
        defaultValue,
        onChange,
        onBlur,
        onInput,
        isMulti,
        extraContent,
        menuFooterContent,
        closeMenuOnSelect = true,
        placeholder = t`Select...`,
        addAction,
        isClearable,
        controlledValue,
        customSelectPropsStyles,
    } = props;
    const direction = useAppSelector(selectDirection);
    const lang = useAppSelector(selectCurrentLang);
    const selectRef = useRef<any>(null);
    const [value, setValue] = useState<SelectOptionT>(null);

    const optionsWithValue = useMemo(() => {
        return value ? [...options, value] : options;
    }, [value, options]);

    const handleNewlyCreatedOption = useCallback((e) => {
        const option = {
            value: e.detail.id,
            label: e.detail.name,
        };
        setValue(option);
        onChange(option);
    }, []);

    useEffect(() => {
        document.addEventListener(`set${name}`, handleNewlyCreatedOption);

        return () => {
            document.removeEventListener(`set${name}`, handleNewlyCreatedOption);
        }
    }, []);

    const handleBlur = (e: any) => {
        if (isMobileOrTablet()) {
            e.preventDefault();
        }
        onBlur && onBlur();
    };

    const handleChange = (val: any, field: any) => {
        onChange(val);
        // For multi-select, val is an array, for single select it's a single object
        if (isMulti && Array.isArray(val)) {
            field.onChange(val);
        } else {
            field.onChange(optionsWithValue?.find(c => c?.value === (val as SelectOptionT)?.value));
        }
    };

    // custom dropdown arrow component
    const DropdownIndicator = (props: any) => {
        return (
            components.DropdownIndicator && (
                <components.DropdownIndicator {...props}>
                    <Icon name='selectFormArrow' color={IconColorE.GRAY} />
                </components.DropdownIndicator>
            )
        );
    };

    const Option = (props: any) => (
        components.Option && (
            <components.Option {...props}>
                {
                    props.data.data ? (
                        <div className={styles.customLabelWrapper}>
                            <p className={styles.labelTitle}>{props.data.data.value1 ? `${props.data.data.value1} - ${props.data.label.split('-')[0]?.trim()}` : props.data.label}</p>
                            <p className={styles.labelDescription}>{props.data.data.value2}</p>
                        </div>
                    ) : (
                        <div className={styles.customLabelWrapper}>
                            <p className={styles.labelTitle}>{props.data.label}</p>
                        </div>
                    )
                }
            </components.Option>
        )
    );

    // shows extra button if it is necessary
    // const NoOptionsMessage = ({ ...props }) => {
    //     const onClick = () => {
    //         createNewOption && createNewOption(props.selectProps.inputValue);
    //         props.selectProps.onMenuClose();
    //     };

    //     return (
    //         <div className={styles.noOptionsWrapper}>
    //             {createNewOption ? (
    //                 <Button
    //                     icon='addCircleFilled'
    //                     mode={ButtonModeE.LINK}
    //                     onClick={onClick}
    //                     withoutTranslate
    //                 >
    //                     <Span>Create a new {label?.toLowerCase()} {props.selectProps.inputValue && `called '`}</Span>{props.selectProps.inputValue}
    //                 </Button>
    //             ) : (
    //                 <Span>No options</Span>
    //             )}
    //         </div>
    //     )
    // };

    // const Menu = ({ innerRef, innerProps, isDisabled, children, ...props }: any) => (
    //     <div ref={innerRef} {...innerProps} className={styles.menuWrapper}>
    //         {children}
    //         <div className={styles.dropdownMenuFooter}>
    //             {!!menuFooterContent && menuFooterContent(props)}
    //         </div>
    //     </div >
    // );

    return (
        <div key={name} style={{ order: orderIndex, ...style }}>
            <Label label={label} name={name} hintText={hintText} addAction={addAction}>
                <Controller
                    name={name as `${string}`}
                    control={control}
                    defaultValue={defaultValue}
                    render={({ field }) => (
                        <Select
                            defaultValue={defaultValue}
                            ref={field.ref}
                            options={optionsWithValue}
                            value={controlledValue || value || optionsWithValue?.find(c => c?.value === field?.value)}
                            placeholder={t(placeholder)}
                            components={{
                                DropdownIndicator,
                                Option,
                                // Menu,
                                // NoOptionsMessage,
                                //@ts-ignore
                                MenuList: (props: MenuListProps) => <MenuList {...props} label={label} createNewOption={createNewOption} />,
                            }}
                            onBlur={handleBlur}
                            styles={errorMessage 
                                ? customSelectInputStylesError(lang) 
                                : (customSelectPropsStyles ? customSelectPropsStyles(lang) : customSelectInputStyles(lang))}
                            menuPortalTarget={document.body}
                            isDisabled={innerProps?.disabled}
                            onChange={(value) => {
                                setValue(null);
                                handleChange(value, field);
                            }}
                            onInputChange={(value, { action }) => {
                                if (action === 'input-change') onInput(value);
                            }}
                            isMulti={isMulti as any}
                            closeMenuOnSelect={closeMenuOnSelect}
                            isRtl={direction === DirectionE.RTL}
                            isClearable={isClearable}
                            {...innerProps}
                            backspaceRemovesValue
                            closeMenuOnScroll
                            menuShouldBlockScroll
                            menuShouldScrollIntoView
                            blurInputOnSelect={false}
                            filterOption={(option, inputValue) => option.label.toLowerCase().trim().includes(inputValue.toLowerCase().trim())}
                        // backspaceRemovesValue={true}
                        />
                    )
                    }
                />
            </Label>
            <ErrorText text={errorMessage} />
            {!!extraContent && extraContent()}
        </div>
    );
};

export default SelectField;